import { createContext, useCallback, useEffect, useState } from 'react'

import { CollectionPageContents_collection, ContentPage } from 'globalTypes'
import { toInteger } from 'lodash'

import { useLazyQuery } from '@apollo/client'
import includes from 'lodash/includes'
import replace from 'lodash/replace'
import { Router, useRouter } from 'next/router'

import QUERY_COLLECTION_ID from './collection-context-id.graphql'
import QUERY_POSITION from './content-get-current-position.graphql'
import QUERY_NEXT from './content-get-next.graphql'
import QUERY_PREV from './content-get-previous.graphql'

interface ContextProps {
  collection?: CollectionPageContents_collection
  collectionSlug?: string
  contents: string[]
  nextElement?: ContentPage
  previousElement?: ContentPage
  updateCollectionData?: (collection: any) => void
  refetchNext?: Function
}

export const Context = createContext<ContextProps>({
  collection: undefined,
  collectionSlug: undefined,
  contents: undefined,
  nextElement: undefined,
  previousElement: undefined,
  refetchNext: () => {},
})
Context.displayName = 'CollectionContext'

export const Provider = Context.Provider
export const Consumer = Context.Consumer

interface Props {
  children: React.ReactNode | React.ReactNode[]
}

const CollectionContext = ({ children }: Props) => {
  const { content_identifier } = useRouter().query

  const [invoked, setInvoked] = useState<boolean>(false)
  const [collection, setCollection] = useState<
    CollectionPageContents_collection | undefined
  >()
  const [collectionSlug, setCollectionSlug] = useState<string | undefined>()
  const [collectionId, setCollectionid] = useState<string | undefined>()
  const [contents, setContents] = useState<string[]>([])
  const [nextElement, setNextElement] = useState<ContentPage | undefined>()
  const [previousElement, setPreviousElement] = useState<
    ContentPage | undefined
  >()
  const [position, setPosition] = useState<number | undefined>()

  const [getCurrentPosition, { loading }] = useLazyQuery(QUERY_POSITION, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (!!data) {
        if (isCollectionContentPage()) {
          const position = toInteger(JSON.parse(data?.getPosition)?.position)
          setPosition(position)
          previous(position)
          next(position)
        }
      }
    },
  })

  const [getCollectionId, { loading: loadId }] = useLazyQuery(
    QUERY_COLLECTION_ID,
    {
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        setCollectionid(data?.collection?.id)
      },
    }
  )

  useEffect(() => {
    !!collectionId &&
      !!content_identifier &&
      getCurrentPosition({
        variables: {
          collectionIdentifier: collectionId,
          contentIdentifier: content_identifier,
        },
      })
  }, [collectionId, getCurrentPosition, content_identifier])

  const [getPrevious, { loading: loadPrev }] = useLazyQuery(QUERY_PREV, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setPreviousElement(
        data.getPreviousContent ? data.getPreviousContent : null
      )
    },
    onError(error) {
      console.log('[ERROR]', error)
    },
  })

  const [getNext, { loading: loadNext }] = useLazyQuery(QUERY_NEXT, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setNextElement(data.getNextContent ? data.getNextContent : null)
    },
    onError(error) {
      console.log('[ERROR]', error)
    },
  })

  async function previous(position: number) {
    try {
      await getPrevious({
        variables: {
          collectionIdentifier: collectionId,
          position: position - 1,
          currentContentIdentifier: content_identifier,
        },
      })
    } catch (error) {
      console.log(error)
    }
  }

  async function next(position: number) {
    try {
      await getNext({
        variables: {
          collectionIdentifier: collectionId,
          position: position + 1,
          currentContentIdentifier: content_identifier,
        },
      })
    } catch (error) {
      console.log(error)
    }
  }

  const setCollectionBySlugId = useCallback(
    (slug) => {
      setCollectionSlug(slug)
      setContents([])

      setPreviousElement(undefined)
      setNextElement(undefined)

      if (Boolean(slug)) {
        getCollectionId({
          variables: {
            identifier: slug,
          },
        })
      }
    },

    [getCollectionId]
  )

  const updateCollectionData = useCallback(
    (collection) => setCollection(collection),
    []
  )

  const getContentSlugFromUrl = () => {
    const path = replace(window.location.href, window.location.origin, '')

    return path.match(/\/([^\/]+)\/?$/)[1]
  }

  const isCollectionContentPage = () => {
    const path = replace(window.location.href, window.location.origin, '')

    return /\/cc?\/([^\?]+(?=\/))/.test(path)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const track = async () => {
    setInvoked(true)

    const path = replace(window.location.href, window.location.origin, '')
    const isCollectionPage =
      /^\/cc\/.+/.test(path) && !/\/cc?\/([^\?]+(?=\/))/.test(path)
    const isCollectionContentPage = /\/cc?\/([^\?]+(?=\/))/.test(path)
    const isContentPage = /^\/c\/.+/.test(path)

    const getCollectionSlugFromUrl = () => {
      if (isCollectionContentPage) {
        return path.match(/\/cc?\/([^\?]+(?=\/))/)?.[1]
      }

      return path.match(/\/cc?\/([^\?]+)/)[1]
    }

    if (isCollectionContentPage) {
      const slug = getCollectionSlugFromUrl()

      return setCollectionBySlugId(slug)
    }

    if (isCollectionPage) {
      const slug = getCollectionSlugFromUrl()

      setCollectionBySlugId(slug)
      return
    }

    if (isContentPage) {
      const slug = getCollectionSlugFromUrl()

      if (includes(contents, slug)) {
        await previous(position)
        await next(position)
        return
      }
    }

    if (!loading) {
      setCollectionBySlugId(undefined)
    }
  }

  useEffect(() => {
    if (!invoked) {
      track()
    }

    Router.events.on('routeChangeComplete', track)

    return () => {
      Router.events.off('routeChangeComplete', track)
    }
  }, [invoked, track])

  function refetchNext() {
    getCurrentPosition({
      variables: {
        collectionIdentifier: collectionId,
        contentIdentifier: content_identifier,
      },
    })
  }

  const value = {
    collection,
    collectionSlug,
    contents,
    nextElement,
    previousElement,
    updateCollectionData,
    refetchNext,
  }

  return <Provider value={value}>{children}</Provider>
}

export default CollectionContext
